import type { RecordBase } from './model';
import type { CollectionPlan } from './campaigns';

export interface CollectionPublication {
  title: string;
  account: string;
  url: string;
}

export interface CollectionJob extends RecordBase {
  campaignId: string;
  date: string;
  slot: string;
  kind?: 'slot' | 'd7';
  dueAt?: string;
  delaySeconds?: number;
  status: 'running' | 'succeeded' | 'partial' | 'failed' | 'skipped' | 'missed';
  attempts: number;
  /** Total attempt ceiling; explicit retries extend it without resetting attempts. */
  attemptLimit?: number;
  retryRequests?: {
    requestedAt: string;
    requestedBy: string;
    previousStatus: 'partial' | 'failed' | 'skipped';
    previousAttempts: number;
    previousError: string;
  }[];
  nextAttemptAt: string | null;
  leaseUntil: string | null;
  lastError: string;
  items: {
    key: string;
    publication?: CollectionPublication;
    status: 'pending' | 'succeeded' | 'failed' | 'skipped' | 'not_applicable';
    collectionKey?: string;
    snapshotId?: string;
    collectedAt?: string;
    closingSnapshot?: boolean;
    error?: string;
  }[];
}
export interface ClosingSnapshot extends RecordBase {
  publication?: CollectionPublication;
  campaignId: string;
  mediaKey: string;
  snapshotId: string;
  publishedAt: string;
  collectedAt: string;
  ageSeconds: number;
  kind: 'first_lifetime_snapshot_after_d7';
  dueAt?: string;
  delaySeconds?: number;
}
export interface CollectionPending extends RecordBase {
  campaignId: string;
  publicationId: string;
  contentId: string;
  url: string;
  reason: 'publication_time_required';
  status: 'pending' | 'resolved';
  message: string;
  resolvedAt?: string;
}
export interface CollectionHistory {
  plan: CollectionPlan;
  jobs: CollectionJob[];
  closings: ClosingSnapshot[];
  pending: CollectionPending[];
  nextDueAt: string | null;
}

/** Meta returns Story insights only while the Story is live: 24 hours after publication. */
export const storyInsightWindowMs = 24 * 60 * 60 * 1000;
export const storyOutsideWindowMessage =
  'Story com mais de 24 horas: a Meta não fornece mais métricas. A última leitura válida foi preservada.';
export const noApplicableItemsMessage =
  'Não se aplica: a Meta só fornece métricas de Story nas primeiras 24 horas.';
/** Not applicable (spec §7), not a collection error: no provider call, no retry. */
export function storyInsightsExpired(
  media: { productType?: string; publishedAt: string },
  now: number,
) {
  const published = Date.parse(media.publishedAt);
  return (
    media.productType === 'STORY' &&
    Number.isFinite(published) &&
    now >= published + storyInsightWindowMs
  );
}
/** Final item states: a saved reading or a publication outside the provider's reading window. */
export const collectionItemSettled = (item: Pick<CollectionJob['items'][number], 'status'>) =>
  item.status === 'succeeded' || item.status === 'not_applicable';
/** Nothing in the job could be read by design; not a failure of the collection plan. */
export const collectionJobNotApplicable = (job: Pick<CollectionJob, 'items'>) =>
  !!job.items.length && job.items.every((item) => item.status === 'not_applicable');
export const collectionJobRetryable = (job: Pick<CollectionJob, 'items'>) =>
  !job.items.length || job.items.some((item) => !collectionItemSettled(item));
